import { useState } from 'react';
import { Link } from 'react-router-dom';
import { cities } from '../data/cafes';

export default function Cafes() {
  const [activeCity, setActiveCity] = useState('hyd');
  const [activeCafe, setActiveCafe] = useState(0);

  const city = cities.find(c => c.id === activeCity);
  const cafe = city.cafes[activeCafe];

  function handleCityChange(id) {
    setActiveCity(id);
    setActiveCafe(0);
  }

  return (
    <div className="bg-cream page-enter">

     {/* Hero */}
      <div className="relative bg-maroon/20 overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Video Background */}
        <video
          key={city.video}
          autoPlay
          loop
          muted
          playsInline
          poster={city.img} 
          className="absolute inset-0 w-full h-full object-cover opacity-90 transition-opacity duration-700"
        >
          <source src={city.video} type="video/mp4" />
        </video>

        <div className="relative z-10 px-8 md:px-16 py-20">
          <p className="text-[10px] tracking-[0.25em] uppercase text-white bold mb-5">Find Your Roastery</p>
          <h1 className="font-serif font-light text-light font-[900] leading-tight mb-5" style={{ fontSize: 'clamp(2.5rem,5vw,4rem)' }}>
            11 Cafés,<br /><em className="text-white">One Soul</em>
          </h1>
          <p className="text-[22px] text-white leading-[1.5] max-w-md mb-10">
            Each Roastery is designed around the city it calls home,
            same beans, same warmth, different story.
          </p>
          <div className="flex gap-12 bold-[500]">
            {[['6','Cities'],['11','Cafés'],['1','In Europe']].map(([n,l]) => (
              <div key={l}>
                <div className="font-serif text-3xl font-light text-light">{n}</div>
                <div className="text-[10px] tracking-widest uppercase bold-[900] text-sand mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* City tabs */}
      <div className="flex border-b border-maroon/10 overflow-x-auto bg-offwhite">
        {cities.map(c => (
          <button key={c.id} onClick={() => handleCityChange(c.id)}
            className={`px-6 md:px-8 py-5 text-[10px] tracking-[0.15em] uppercase whitespace-nowrap border-b-2 transition-all duration-300 font-medium ${
              activeCity === c.id
                ? 'border-maroon text-maroon'
                : 'border-transparent text-maroon/35 hover:text-maroon/65'
            }`}>
            {c.name}
          </button>
        ))}
      </div>

      {/* City panel */}
      <div className="px-8 md:px-16 pt-14 pb-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12 pb-8 border-b border-maroon/8">
          <h2 className="font-serif text-3xl font-light text-maroon">
            <em className="not-italic">{city.name}</em>
            <span className="text-maroon/30 font-light"> — {city.subtitle}</span>
          </h2>
          <p className="text-sm text-maroon/45 max-w-sm leading-[1.8]">{city.desc}</p>
        </div>

        {city.cafes.length > 1 && (
          <div className="flex gap-2 mb-8">
            {city.cafes.map((c, i) => (
              <button key={i} onClick={() => setActiveCafe(i)}
                className={`text-[10px] tracking-widest uppercase px-4 py-2 border transition-colors duration-300 ${
                  activeCafe === i ? 'bg-maroon text-light border-maroon' : 'text-maroon/50 border-maroon/20 hover:border-maroon/40'
                }`}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Cafe detail + map */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-sand/20 mb-16">

          {/* Left — photo + info */}
          <div className="flex flex-col bg-offwhite">
            <div className="overflow-hidden bg-wine flex-shrink-0" style={{ height: '260px' }}>
              <img src={cafe.img} alt={cafe.name}
                className="w-full h-full object-cover"
                onError={e => { e.target.parentElement.style.background='#6D2932'; e.target.style.display='none'; }} />
            </div>
            <div className={`flex-1 p-10 ${cafe.flagship ? 'bg-maroon' : 'bg-offwhite'}`}>
              <div className="text-[9px] font-medium tracking-[0.2em] uppercase text-sand/50 mb-3">{cafe.tag}</div>
              <h3 className={`font-serif text-2xl font-light mb-4 leading-tight ${cafe.flagship ? 'text-light' : 'text-maroon'}`}>
                {cafe.name}
              </h3>
              <p className={`text-sm mb-7 leading-[1.8] ${cafe.flagship ? 'text-sand/45' : 'text-maroon/50'}`}>
                {cafe.address}
              </p>
              <div className="flex flex-col gap-3.5 mb-7">
                {[
                  { icon: '🕐', val: cafe.hours },
                  { icon: '📞', val: cafe.phone, href: `tel:${cafe.phone.replace(/\s/g,'')}` },
                  { icon: '📍', val: cafe.note },
                ].map(({ icon, val, href }) => (
                  <div key={val} className={`flex items-start gap-3 text-sm ${cafe.flagship ? 'text-sand/50' : 'text-maroon/50'}`}>
                    <span className="w-5 flex-shrink-0 mt-0.5 opacity-60">{icon}</span>
                    {href ? <a href={href} className="hover:opacity-100 transition-opacity">{val}</a> : <span>{val}</span>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap mb-8">
                {cafe.tags.map(t => (
                  <span key={t} className={`text-[9px] tracking-wide px-3 py-1 rounded-full ${cafe.flagship ? 'bg-sand/10 text-sand/55' : 'bg-maroon/6 text-maroon/50'}`}>{t}</span>
                ))}
              </div>
              <div className="flex gap-3 flex-wrap">
                <a href={cafe.mapsUrl} target="_blank" rel="noreferrer"
                  className={`text-[10px] font-medium tracking-[0.12em] uppercase px-6 py-3 transition-colors duration-300 ${
                    cafe.flagship ? 'bg-sand text-maroon hover:bg-light' : 'bg-maroon text-light hover:bg-wine'
                  }`}>
                  Open in Google Maps ↗
                </a>
                <a href={`tel:${cafe.phone.replace(/\s/g,'')}`}
                  className={`text-[10px] font-medium tracking-[0.12em] uppercase px-6 py-3 border transition-colors duration-300 ${
                    cafe.flagship ? 'border-sand/20 text-sand/70 hover:bg-sand/10' : 'border-maroon/20 text-maroon/60 hover:bg-maroon/5'
                  }`}>
                  Call Café
                </a>
              </div>
            </div>
          </div>

          {/* Right — Google Maps */}
          <div className="min-h-[560px] bg-sand/20 overflow-hidden">
            <iframe key={cafe.name} title={`Map — ${cafe.name}`}
              src={cafe.embedSrc} width="100%" height="100%"
              style={{ minHeight:'560px', border:0, display:'block' }}
              allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </div>

      {/* Helsinki strip */}
      <div className="bg-wine mx-8 md:mx-16 mb-16 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
        <div className="px-12 py-14">
          <p className="text-[10px] tracking-[0.2em] uppercase text-sand/40 mb-4">Beyond India</p>
          <h3 className="font-serif text-3xl font-light text-light leading-tight mb-4">
            Now Brewing in<br /><em className="text-sand/70">Helsinki, Finland</em>
          </h3>
          <p className="text-sm text-sand/45 leading-[1.8] max-w-sm mb-8">
            The first Indian coffee chain in the Nordic market — putting Indian specialty coffee on the global map since 2024.
          </p>
          <a href="https://www.google.com/maps/search/Roastery+Coffee+House+Helsinki+Finland"
            target="_blank" rel="noreferrer"
            className="inline-block text-[10px] font-medium tracking-[0.15em] uppercase px-6 py-3 bg-cream text-maroon hover:bg-light transition-colors duration-300">
            View on Maps ↗
          </a>
        </div>
        <div className="min-h-[280px]">
          <iframe title="Helsinki" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1984.2!2d24.9384!3d60.1699!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46920a34cde0a6b5%3A0xa5a2aad59e1fb2c3!2sHelsinki!5e0!3m2!1sen!2sin!4v1710000000010"
            width="100%" height="100%" style={{ minHeight:'280px', border:0, display:'block' }}
            allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div>
    </div>
  );
}
